import { Box, Tab, Tabs } from "@mui/material";
import { ReactNode, SyntheticEvent, useState } from "react";
import { MedicalNotes } from "./components/MedicalNotes";
import { Solicitations } from "./components/Solicitations";
import { NoteReport } from "./components/NoteReport";
import { Procedures } from "./components/Procedures";
import { IframeDialog } from "../../IframeDialog";
import { useFhirData } from "../../../../../utils/useFhirData";
import { useDocumentDialog } from "../../../../../hooks/useDocumentDialog";
import {
  processMedicalNotes,
  processSolicitations,
  processNoteReports,
  filterByCategory,
} from "../../../../../hooks/useDocumentProcessing";
import { filterBySignStatus } from "../../../../../utils/fhirDocumentUtils";
import { TabLabel } from "./components/TabLabel";

interface TabPanelProps {
  children?: ReactNode;
  index: number;
  value: number;
}

function a11yProps(index: number) {
  return {
    id: `simple-tab-${index}`,
    "aria-controls": `simple-tabpanel-${index}`,
  };
}

function CustomTabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ maxHeight: "60vh", pt: 2, overflow: "auto" }}>
          {children}
        </Box>
      )}
    </div>
  );
}

export function PatientDocs({
  medicalNoteData,
  solicitationData,
  resultsData,
  NoteReportData,
  setRefetch,
  sign,
  summaryData,
}: any) {
  // Gerencia estado das tabs
  const [selectedTab, setSelectedTab] = useState(0);
  const handleChange = (event: SyntheticEvent, newValue: number) => {
    setSelectedTab(newValue);
  };

  // Gerencia estado do dialog de visualização usando hook reutilizável
  const { open, binaryUrl, setBinaryUrl, handleOpen, handleClose } =
    useDocumentDialog();

  // Extrai MedicationRequest dos dados de prescrição
  const medicationRequest = medicalNoteData?.filter(
    (el: any) => el?.resource?.resourceType === "MedicationRequest",
  );

  // Extrai ServiceRequest dos dados de solicitação
  const serviceRequest = solicitationData?.filter(
    (el: any) => el?.resource?.resourceType === "ServiceRequest",
  );

  // Processa dados de prescrições médicas usando função utilitária
  const medicalNote = filterBySignStatus(medicalNoteData, sign);
  const rowData = processMedicalNotes(
    medicalNoteData,
    medicationRequest,
    sign,
  );

  // Processa dados de solicitações de exame usando função utilitária
  const solicitation = solicitationData?.filter((e: any) => {
    const isSigned =
      e?.resource?.securityLabel?.[0]?.coding?.[0]?.code === "ProofOfOrigin";
    const isSolicitation =
      e?.resource?.category?.[0]?.coding?.[0]?.display === "Solicitação";
    return sign
      ? isSigned && isSolicitation
      : e?.resource?.resourceType === "DocumentReference" &&
          !isSigned &&
          isSolicitation;
  });

  const rows = processSolicitations(
    solicitationData,
    serviceRequest,
    resultsData,
    sign,
  );

  // Processa dados de relatórios e atestados usando função utilitária
  const noteReport = filterBySignStatus(NoteReportData, sign);
  const noteReportRowData = processNoteReports(NoteReportData, sign);

  // Filtra procedimentos por categoria
  const proceduresData = filterByCategory(solicitationData, "Procedimento");

  return (
    <>
      <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
        <Tabs
          value={selectedTab}
          onChange={handleChange}
          aria-label="basic tabs example"
          variant="scrollable"
        >
          <Tab
            label={
              <TabLabel
                label="Receitas médicas"
                count={medicalNote?.length || "0"}
              />
            }
            {...a11yProps(0)}
          />
          <Tab
            label={
              <TabLabel
                label="Solicitações de exame"
                count={solicitation?.length || "0"}
              />
            }
            {...a11yProps(1)}
          />
          <Tab
            label={
              <TabLabel
                label="Procedimentos"
                count={proceduresData?.length || "0"}
              />
            }
            {...a11yProps(2)}
          />
          <Tab
            label={
              <TabLabel
                label="Relatórios e atestados"
                count={noteReport?.length || "0"}
              />
            }
            {...a11yProps(3)}
          />
        </Tabs>
      </Box>

      <CustomTabPanel value={selectedTab} index={0}>
        <MedicalNotes
          sign={sign}
          rowData={rowData}
          handleOpen={handleOpen}
          setBinaryUrl={setBinaryUrl}
        />
      </CustomTabPanel>
      <CustomTabPanel value={selectedTab} index={1}>
        <Solicitations
          setRefetch={setRefetch}
          sign={sign}
          handleOpen={handleOpen}
          setBinaryUrl={setBinaryUrl}
          rowData={rows}
          resultsData={resultsData}
        />
      </CustomTabPanel>
      <CustomTabPanel value={selectedTab} index={2}>
        <Procedures
          setRefetch={setRefetch}
          sign={sign}
          handleOpen={handleOpen}
          setBinaryUrl={setBinaryUrl}
          data={proceduresData || []}
          resultsData={resultsData}
          procedureServiceRequests={useFhirData.extractPatientSummaryResource(
            summaryData,
            "ServiceRequest",
          )}
        />
      </CustomTabPanel>

      <CustomTabPanel value={selectedTab} index={3}>
        <NoteReport
          sign={sign}
          rowData={noteReportRowData}
          handleOpen={handleOpen}
          setBinaryUrl={setBinaryUrl}
        />
      </CustomTabPanel>

      {open && (
        <IframeDialog
          setRefetchSummary={setRefetch}
          handleClose={handleClose}
          isOpen={open}
          data={binaryUrl}
        />
      )}
    </>
  );
}
